import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { css } from '@emotion/core'
import { colors, Ellipsis } from '@fs/zion-ui'
import { string, number, oneOfType, shape, arrayOf, func } from 'prop-types'

let globalIdCounter = 0

const getToggleStyles = (toggleBackground) => css`
  display: flex;
  width: 100%;
  padding: 4px;
  justify-content: space-between;
  align-items: center;
  border-radius: 9999px;
  background: ${toggleBackground};
  position: relative;
  overflow: hidden;
`

const getButtonStyles = (
  actualUnselectedTextColor,
  actualSelectedTextColor,
  animationDuration,
  focusOutlineColor
) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  min-width: 50%;
  background: transparent;
  color: ${actualUnselectedTextColor};
  font-size: 14px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 9999px;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: color ${animationDuration}ms ease;

  &.selected {
    color: ${actualSelectedTextColor};
  }

  /* Remove default focus outline for all focus events */
  &:focus {
    outline: none;
  }

  /* Show outline only for keyboard navigation */
  &:focus-visible {
    outline: 2px solid ${focusOutlineColor};
    outline-offset: 2px;
    z-index: 1;
  }
`

const radioInputCss = css`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`

const WideToggle = ({
  options,
  selectedValue = 0,
  onSelect = () => {},
  ariaLabel,
  toggleBackground = colors.gray00,
  buttonTextColor = colors.gray100,
  selectedButtonBackground = colors.blue05,
  focusOutlineColor = colors.blue60,
  selectedTextColor,
  unselectedTextColor,
  animationDuration = 250, // Animation duration in ms
}) => {
  const toggleRef = useRef(null)
  const buttonRefs = useRef([])
  const inputRefs = useRef([])
  const groupName = useMemo(() => {
    globalIdCounter += 1
    return `wide-toggle-${globalIdCounter}`
  }, [])

  // Single state for animation control - starts false to prevent initial animation
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [pillPosition, setPillPosition] = useState(null)

  // Use actualUnselectedTextColor if provided, otherwise fall back to buttonTextColor
  const actualUnselectedTextColor = unselectedTextColor || buttonTextColor
  // Use selectedTextColor if provided, otherwise use the same as unselected
  const actualSelectedTextColor = selectedTextColor || actualUnselectedTextColor

  /**
   * Updates the position of the selection pill based on the selected button
   */
  const updatePillPosition = useCallback(() => {
    // Ensure selected index is valid
    const currentIndex = selectedValue >= 0 && selectedValue < options.length ? selectedValue : 0
    const selectedButton = buttonRefs.current[currentIndex]
    const toggleElement = toggleRef.current

    if (selectedButton && toggleElement) {
      const toggleRect = toggleElement.getBoundingClientRect()
      const buttonRect = selectedButton.getBoundingClientRect()

      // Calculate position relative to the container
      setPillPosition({
        left: buttonRect.left - toggleRect.left,
        width: buttonRect.width,
      })
    }
  }, [selectedValue, options.length])

  // Track previous selected value to determine if this is a user change
  const prevSelectedValueRef = useRef(selectedValue)

  // Set up resize observer and handle initial positioning
  useEffect(() => {
    // Update position immediately for first render
    updatePillPosition()

    // Set up resize observer for responsive updates
    const resizeObserver = new ResizeObserver(updatePillPosition)

    if (toggleRef.current) {
      resizeObserver.observe(toggleRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [updatePillPosition])

  // Enable animations only when selected value changes after initial render
  useEffect(() => {
    // Skip the first render
    if (prevSelectedValueRef.current !== selectedValue) {
      setShouldAnimate(true)
    }

    // Update ref for next comparison
    prevSelectedValueRef.current = selectedValue
  }, [selectedValue])

  const handleToggle = (index) => {
    onSelect(options[index])
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      handleToggle(index)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      const nextIndex = (index + 1) % options.length
      handleToggle(nextIndex)
      // Focus the next button
      setTimeout(() => {
        inputRefs.current[nextIndex]?.focus()
      }, 0)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevIndex = (index - 1 + options.length) % options.length
      handleToggle(prevIndex)
      // Focus the previous button
      setTimeout(() => {
        inputRefs.current[prevIndex]?.focus()
      }, 0)
    }
  }

  // Styles
  const toggleStyles = getToggleStyles(toggleBackground)

  const buttonStyles = getButtonStyles(
    actualUnselectedTextColor,
    actualSelectedTextColor,
    animationDuration,
    focusOutlineColor
  )

  const pillStyles = css`
    position: absolute;
    height: calc(100% - 8px);
    border-radius: 9999px;
    background-color: ${selectedButtonBackground};
    opacity: ${pillPosition ? 1 : 0};
    top: 4px;
    inset-inline-start: 0;
    z-index: 0;
  `

  // Simplified transition style
  const pillTransitionStyles = shouldAnimate
    ? { transition: `transform ${animationDuration}ms ease, width ${animationDuration}ms ease` }
    : { transition: 'none' }

  return (
    <div css={toggleStyles} role="radiogroup" aria-label={ariaLabel} ref={toggleRef}>
      {pillPosition && (
        <div
          css={pillStyles}
          style={{
            width: `${pillPosition.width}px`,
            transform: `translateX(${pillPosition.left}px)`,
            ...pillTransitionStyles,
          }}
          aria-hidden="true"
        />
      )}
      {options.map((option, index) => (
        <label
          key={option.value}
          css={buttonStyles}
          className={selectedValue === index ? 'selected' : ''}
          ref={(el) => {
            buttonRefs.current[index] = el
          }}
        >
          <input
            type="radio"
            name={groupName}
            value={option.value}
            checked={selectedValue === index}
            onChange={() => handleToggle(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            css={radioInputCss}
            className={selectedValue === index ? 'selected' : ''}
            tabIndex={selectedValue === index ? 0 : -1}
          />
          <Ellipsis>{option.label}</Ellipsis>
        </label>
      ))}
    </div>
  )
}

WideToggle.propTypes = {
  options: arrayOf(
    shape({
      label: string.isRequired,
      value: oneOfType([string, number]).isRequired,
    })
  ).isRequired,
  selectedValue: number,
  onSelect: func,
  ariaLabel: string,
  toggleBackground: string,
  buttonTextColor: string,
  selectedButtonBackground: string,
  focusOutlineColor: string,
  selectedTextColor: string,
  unselectedTextColor: string,
  animationDuration: number,
}

export default WideToggle
